'use client';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import KeyboardArrowDownOutlined from '@mui/icons-material/KeyboardArrowDownOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { getScrollParent, isScrollbarClick } from '../../utils/scrollParent';
import styles from './FilterChip.module.css';

export interface FilterOption {
	value: string;
	label: string;
	countsAsActive?: boolean;
	hideCustomAction?: boolean;
}

export interface FilterGroup {
	label: string;
	options: FilterOption[];
	selectionMode?: 'single' | 'multiple';
	presentation?: 'radio';
	searchable?: boolean;
}

export interface CustomPanelControls {
	close: () => void;
	activate: () => void;
	deactivate: () => void;
}

export type CustomPanelRenderer = (controls: CustomPanelControls) => ReactNode;

export interface FilterChipProps {
	values: string[];
	onChange: (values: string[]) => void;
	groups: FilterGroup[];
	placeholder?: string;
	customPanels?: Record<string, CustomPanelRenderer>;
}

export const FilterChip = ({
	values,
	onChange,
	groups,
	placeholder = 'Filtros',
	customPanels,
}: FilterChipProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const inputGroupId = useId();
	const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
	const [activeCustomPanelValue, setActiveCustomPanelValue] = useState<string | null>(null);
	const [groupSearches, setGroupSearches] = useState<Record<string, string>>({});
	const [overlayPos, setOverlayPos] = useState({ top: 0, left: 0 });
	const [overlayHost, setOverlayHost] = useState<HTMLElement | null>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const customPanelRef = useRef<HTMLDivElement>(null);

	const isSingleGroup = groups.length === 1;
	const activeCount = values.filter((value) =>
		groups.some((group) =>
			group.options.some((option) => option.value === value && option.countsAsActive !== false)
		)
	).length;
	const isActive = activeCount > 0;
	const showCustomPanel = activeCustomPanelValue !== null;
	const isAnyOpen = isOpen || showCustomPanel;

	useEffect(() => {
		if (!isOpen && !showCustomPanel) return;
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node;
			const inWrapper = wrapperRef.current?.contains(target);
			const inDropdown = dropdownRef.current?.contains(target);
			const inPanel = customPanelRef.current?.contains(target);
			if (!inWrapper && !inDropdown && !inPanel && !isScrollbarClick(event)) {
				setIsOpen(false);
				setActiveCustomPanelValue(null);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen, showCustomPanel]);

	useEffect(() => {
		if (isOpen && isSingleGroup && groups.length > 0) {
			setExpandedGroups([groups[0].label]);
		}
	}, [isOpen, isSingleGroup, groups]);

	useLayoutEffect(() => {
		if (!isAnyOpen) return;
		const scrollParent = getScrollParent(wrapperRef.current);
		const host = document.createElement('div');
		host.className = styles.overlayHost;
		(scrollParent === document.documentElement ? document.body : scrollParent).appendChild(host);
		setOverlayHost(host);
		return () => {
			host.remove();
			setOverlayHost(null);
		};
	}, [isAnyOpen]);

	useLayoutEffect(() => {
		if (!overlayHost) return;

		const updatePositions = () => {
			if (!wrapperRef.current) return;
			const rect = wrapperRef.current.getBoundingClientRect();
			const hostRect = overlayHost.getBoundingClientRect();
			const overlay = isOpen ? dropdownRef.current : customPanelRef.current;
			const overlayWidth = overlay?.getBoundingClientRect().width ?? 0;
			const viewportMargin = 8;
			const left = Math.max(
				viewportMargin,
				Math.min(rect.left, window.innerWidth - overlayWidth - viewportMargin)
			);
			const pos = { top: rect.bottom - hostRect.top, left: left - hostRect.left };
			setOverlayPos((prev) => (prev.top === pos.top && prev.left === pos.left ? prev : pos));
		};

		updatePositions();

		window.addEventListener('scroll', updatePositions, true);
		window.addEventListener('resize', updatePositions);
		return () => {
			window.removeEventListener('scroll', updatePositions, true);
			window.removeEventListener('resize', updatePositions);
		};
	}, [
		overlayHost,
		isOpen,
		showCustomPanel,
		activeCustomPanelValue,
		expandedGroups,
		groupSearches,
		groups,
		isActive,
	]);

	const totalOptions = groups.reduce((sum, group) => sum + group.options.length, 0);
	if (totalOptions === 0) return null;

	const handleToggle = () => {
		if (isOpen || showCustomPanel) {
			setIsOpen(false);
			setActiveCustomPanelValue(null);
		} else {
			setIsOpen(true);
		}
	};

	const toggleGroup = (groupLabel: string) => {
		setExpandedGroups((prev) =>
			prev.includes(groupLabel)
				? prev.filter((group) => group !== groupLabel)
				: [...prev, groupLabel]
		);
	};

	const openCustomPanel = (optionValue: string) => {
		setIsOpen(false);
		setActiveCustomPanelValue(optionValue);
	};

	const returnToDropdown = () => {
		setActiveCustomPanelValue(null);
		setIsOpen(true);
	};

	const activateOption = (optionValue: string) => {
		const group = groups.find((item) =>
			item.options.some((option) => option.value === optionValue)
		);
		const nextValues = values.filter(
			(value) =>
				value !== optionValue &&
				(group?.selectionMode !== 'single' ||
					!group.options.some((option) => option.value === value))
		);
		onChange([...nextValues, optionValue]);
	};

	const deactivateOption = (optionValue: string) => {
		onChange(values.filter((value) => value !== optionValue));
	};

	const handleOptionChange = (optionValue: string) => {
		const optionCustomPanel = customPanels?.[optionValue];
		if (values.includes(optionValue)) {
			deactivateOption(optionValue);
		} else if (optionCustomPanel) {
			openCustomPanel(optionValue);
		} else {
			activateOption(optionValue);
		}
	};

	const handleClearAll = () => {
		onChange([]);
		setActiveCustomPanelValue(null);
	};

	const activeCustomPanel = activeCustomPanelValue ? customPanels?.[activeCustomPanelValue] : null;

	return (
		<div className={styles.outer} ref={wrapperRef}>
			<button
				type="button"
				className={[
					styles.chip,
					isActive ? styles.chipActive : '',
					isAnyOpen ? styles.chipOpen : '',
				].join(' ')}
				onClick={handleToggle}
			>
				<TuneOutlined className={styles.chipIcon} />
				<span className={styles.chipLabel}>{placeholder}</span>
				{activeCount > 0 && <span className={styles.chipCount}>· {activeCount}</span>}
				<KeyboardArrowDownOutlined
					className={[styles.chipArrow, isAnyOpen ? styles.chipArrowOpen : ''].join(' ')}
				/>
			</button>

			{isOpen &&
				overlayHost &&
				createPortal(
					<div
						ref={dropdownRef}
						className={styles.dropdown}
						style={{
							position: 'absolute',
							top: overlayPos.top,
							left: overlayPos.left,
						}}
					>
						{isActive && (
							<div className={styles.clearRow}>
								<button type="button" className={styles.clearBtn} onClick={handleClearAll}>
									<CloseOutlined className={styles.clearIcon} />
									Limpar filtros
								</button>
							</div>
						)}

						{groups.map((group) => {
							const isRadio = group.presentation === 'radio';
							const isExpanded = isRadio || expandedGroups.includes(group.label) || isSingleGroup;
							const hasActive = values.some((value) =>
								group.options.some(
									(option) => option.value === value && option.countsAsActive !== false
								)
							);
							const isSearchable = group.searchable ?? false;
							const searchValue = groupSearches[group.label] ?? '';
							const visibleOptions =
								isSearchable && searchValue.trim()
									? group.options.filter((option) =>
											option.label.toLowerCase().includes(searchValue.trim().toLowerCase())
										)
									: group.options;

							return (
								<div
									key={group.label}
									className={`${styles.groupContainer} ${isRadio ? styles.radioGroup : ''}`}
								>
									{isRadio ? (
										<span className={styles.groupTitle}>{group.label}</span>
									) : isSingleGroup ? (
										<span className={styles.groupSectionLabel}>{group.label}</span>
									) : (
										<button
											type="button"
											className={styles.groupHeader}
											onClick={() => toggleGroup(group.label)}
										>
											<div className={styles.groupHeaderLeft}>
												<span className={styles.groupTitle}>{group.label}</span>
												{hasActive && <span className={styles.groupDot} />}
											</div>
											<KeyboardArrowDownOutlined
												className={[
													styles.groupArrow,
													isExpanded ? styles.groupArrowExpanded : '',
												].join(' ')}
											/>
										</button>
									)}

									{isExpanded && (
										<>
											{isSearchable && (
												<input
													type="search"
													className={styles.groupSearch}
													value={searchValue}
													onChange={(event) =>
														setGroupSearches((prev) => ({
															...prev,
															[group.label]: event.target.value,
														}))
													}
													placeholder={`Buscar ${group.label.toLowerCase()}...`}
												/>
											)}
											<div
												className={[
													styles.optionsList,
													isSearchable ? styles.optionsListScrollable : '',
												].join(' ')}
											>
												{visibleOptions.map((option) => {
													const optionCustomPanel = customPanels?.[option.value];
													const isChecked = values.includes(option.value);

													if (optionCustomPanel) {
														return (
															<div key={option.value} className={styles.optionLabel}>
																<input
																	type="checkbox"
																	aria-label={option.label}
																	className={styles.checkbox}
																	checked={isChecked}
																	onChange={() => handleOptionChange(option.value)}
																/>
																<button
																	type="button"
																	className={styles.optionTextButton}
																	onClick={() => openCustomPanel(option.value)}
																>
																	{option.label}
																</button>
																{isChecked && !option.hideCustomAction && (
																	<button
																		type="button"
																		className={styles.optionActionButton}
																		onClick={() => openCustomPanel(option.value)}
																	>
																		Trocar
																	</button>
																)}
															</div>
														);
													}

													if (isRadio) {
														return (
															<label key={option.value} className={styles.radioOption}>
																<input
																	type="radio"
																	name={`${inputGroupId}-${group.label}`}
																	className={styles.radioInput}
																	checked={isChecked}
																	onChange={() => activateOption(option.value)}
																/>
																<span>{option.label}</span>
															</label>
														);
													}

													return (
														<label key={option.value} className={styles.optionLabel}>
															<input
																type="checkbox"
																className={styles.checkbox}
																checked={isChecked}
																onChange={() => handleOptionChange(option.value)}
															/>
															<span className={styles.optionText}>{option.label}</span>
														</label>
													);
												})}
												{visibleOptions.length === 0 && (
													<span className={styles.emptyOptions}>Nenhuma opção encontrada.</span>
												)}
											</div>
										</>
									)}
								</div>
							);
						})}
					</div>,
					overlayHost
				)}

			{activeCustomPanelValue !== null &&
				activeCustomPanel &&
				overlayHost &&
				createPortal(
					<div
						ref={customPanelRef}
						className={styles.customPanel}
						style={{
							position: 'absolute',
							top: overlayPos.top,
							left: overlayPos.left,
						}}
					>
						{activeCustomPanel({
							close: returnToDropdown,
							activate: () => activateOption(activeCustomPanelValue),
							deactivate: () => deactivateOption(activeCustomPanelValue),
						})}
					</div>,
					overlayHost
				)}
		</div>
	);
};
